// src/App.jsx
import React, { useEffect, useMemo, useRef } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { store } from './store';
import { getTheme } from './theme';
import { setSidebarPermanent } from './store/slices/uiSlice';
import { useRefreshTokenMutation } from './store/api/authApi';
import AppRoutes from './routes/AppRoutes';
import './styles/globals.css';

// A component with access to the Redux store
const AppContent = () => {
  const dispatch = useDispatch();
  const { mode } = useSelector((state) => state.ui);
  const { sessionChecked } = useSelector((state) => state.auth);
  const [restoreSession] = useRefreshTokenMutation();
  const restoreStarted = useRef(false);

  const theme = useMemo(() => getTheme(mode), [mode]);

  useEffect(() => {
    if (restoreStarted.current || sessionChecked) return;
    restoreStarted.current = true;
    const restore = async () => {
      try {
        await restoreSession().unwrap();
      } catch (error) {
        if (error?.status === 409) {
          try {
            await restoreSession().unwrap();
          } catch {
            // The mutation clears auth state when no valid session exists.
          }
        }
      }
    };
    restore();
  }, [restoreSession, sessionChecked]);

  // Handle responsive sidebar behavior
  useEffect(() => {
    const handleResize = () => {
      const isPermanent = window.innerWidth >= 960; // MD breakpoint
      dispatch(setSidebarPermanent(isPermanent));
    };

    handleResize(); // Set initial state
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [dispatch]);

  if (!sessionChecked) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

// Main App component
const App = () => (
  <Provider store={store}>
    <AppContent />
  </Provider>
);

export default App;
// // src/App.jsx
// import React, { useEffect } from 'react';
// import { BrowserRouter } from 'react-router-dom';
// import { Provider } from 'react-redux';
// import { ThemeProvider, CssBaseline } from '@mui/material';
// import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
// import { useSelector, useDispatch } from 'react-redux';
// import { store } from './store';
// import { getTheme } from './theme';
// import { setSidebarPermanent } from './store/slices/uiSlice';
// import AppRoutes from './routes/AppRoutes';
// import './styles/globals.css';

// // Inner App component that has access to Redux store
// const AppContent = () => {
//   const dispatch = useDispatch();
//   const { mode } = useSelector((state) => state.ui);
  
//   const theme = React.useMemo(() => getTheme(mode), [mode]);

//   // Handle responsive sidebar
//   useEffect(() => {
//     const handleResize = () => {
//       const isPermanent = window.innerWidth >= 960;
//       dispatch(setSidebarPermanent(isPermanent));
//     };

//     handleResize(); // Initial check
//     window.addEventListener('resize', handleResize);
    
//     return () => window.removeEventListener('resize', handleResize);
//   }, [dispatch]);

//   return (
//     <ThemeProvider theme={theme}>
//       <CssBaseline />
//       <LocalizationProvider dateAdapter={AdapterDateFns}>
//         <BrowserRouter>
//           <AppRoutes />
//         </BrowserRouter>
//       </LocalizationProvider>
//     </ThemeProvider>
//   );
// };

// // Main App component with Redux Provider
// const App = () => {
//   return (
//     <Provider store={store}>
//       <AppContent />
//     </Provider>
//   );
// };

// export default App;